//@import dependencies
import React, {FC, useState, useEffect, useCallback} from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
//@end

//@import components
import InputComponent from 'app_components/commons/Input/InputComponent'
import InputFileComponent from 'app_components/commons/InputFileComponent/InputFileComponent'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import IconComponent from 'app_components/commons/icons'
import { RiAddFill } from 'react-icons/ri'
import { AiFillSave } from 'react-icons/ai'
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import { MdDeleteForever } from 'react-icons/md'
import CopyTextComponent from 'app_components/commons/CopyTextComponent/CopyTextComponent'
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent'
//@end

// @import hooks
import {useColors} from 'app_hooks/useColors'
import { I18nUtil } from 'app_utils/i18n'
// @end

// @INFO Import types
import { IAllianceBrochure, IAllianceBrochureCreate } from 'app_types/admin/landings/landingTypes'
// @end

// @INFO Import services
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
import LandingService from 'app_services/admin/landings/landingService'
// @end

// @INFO Import Styles
import './modalAllianceBrochureForm.scss'

const uuid = require('react-uuid')

interface IModalAllianceBrochureFormModuleLayoutProps {
  source: string
  open          : boolean
  allianceUnique: string
  editData?     : IAllianceBrochure[]
  handleClose   : (data:any) => void
}

const ModalAllianceBrochureFormModuleLayout: React.FC<IModalAllianceBrochureFormModuleLayoutProps> = (props) => {

  const landingService = new LandingService()
  const i18nUtil = new I18nUtil()
  const screenNotifications = new ScreenNotificationsUtil()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [brochures, setBrochures] = useState<IAllianceBrochureCreate[]>([])

  const [colors] = useColors()

  const {
    open,
    allianceUnique,
    source,
    editData,
    handleClose,
  } = props

  const { t } = useTranslation()

  useEffect(() => {
    if (editData?.length) {
      setBrochures(editData)
    }
  }, [editData])

  const handleCloseModal = (data: any) => {
    handleClose(data)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    const response = await landingService.saveAllianceBrochure({
      allianceUnique,
      brochures,
      slug: source
    })
    if (response) {
      screenNotifications.fireToast(
        {
          message: `${i18nUtil.getTranslate('components.screen_notifications.update')}`,
        },
        {
          position: 'top',
        }
      )
      handleClose({ status: true, data: response })
    } else {
      handleClose({ status: false })
    }
    setIsLoading(false)
  }

  const handleChangeBrochures = (newBrochure: IAllianceBrochureCreate) => {
    const newBrochures = [...brochures]
    const idx = newBrochures.findIndex((brochure) => brochure.unique === newBrochure.unique)
    if (idx >= 0) {
      newBrochures[idx] = newBrochure
    } else {
      newBrochures.push(newBrochure)
    }
    setBrochures([...newBrochures])
  }

  const handleClickAddBrochure = () => {
    const newBrochure: IAllianceBrochureCreate = {
      unique: uuid(),
      name: '',
    }
    setBrochures([...brochures, newBrochure])
  }

  const handleClickDelete = (unique: string) => {
    const newBrochures = [...brochures]
    const idx = newBrochures?.findIndex((brochure) => brochure.unique === unique)
    if (idx >= 0) {
      newBrochures.splice(idx, 1)
      setBrochures([ ...newBrochures ])
    }
  }

  return(
    <div className=''>
      <Modal
        show={open}
        onHide={() => handleCloseModal({ status: false })}
        className="modal_alliance_brochure_form-module"
        size='lg'
      >
        <Modal.Header>
          <Modal.Title>
            {t('modules.modal_alliance_brochure_form.title')}
          </Modal.Title>
          <div className="close-button" onClick={() => handleCloseModal({status: false})}>
            <IconComponent
              color={colors.secondary}
              width='1.5rem'
              height='1.5rem'
              fontawesome={{
                icon: 'fas fa-times-circle',
              }}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className='modal-brochure--layout'>
            <div className='modal-brochure--header-buttons'>
              <ButtonComponent onClick={handleClickAddBrochure} variant="primary">
                <ButtonComponent.Icon>
                  <RiAddFill />
                </ButtonComponent.Icon>
                Agregar folleto
              </ButtonComponent>
            </div>
            <div className="modal-brochure--brochures-form">
              {brochures?.map((brochure) => (
                <BrochureForm
                  key={brochure.unique}
                  value={brochure}
                  onChange={handleChangeBrochures}
                  onDelete={handleClickDelete}
                />
              ))}
            </div>
            <div className="modal-brochure--footer-buttons">
              <ButtonComponent
                onClick={() => handleCloseModal({ status: false })}
                disabled={isLoading}
              >
                Cancelar
              </ButtonComponent>
              <ButtonComponent
                onClick={handleSubmit}
                loading={isLoading}
                variant='primary'
              >
                <ButtonComponent.Icon>
                  <AiFillSave />
                </ButtonComponent.Icon>
                {t('modules.modal_alliance_brochure_form.form.save')}
              </ButtonComponent>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

interface IBrochureForm {
  value: IAllianceBrochureCreate
  onChange: (data: IAllianceBrochureCreate) => void
  onDelete: (unique: string) => void
}

const BrochureForm: FC<IBrochureForm> = ({ onChange, onDelete, value }) => {

  const [isValidFile, setIsValidFile] = useState<boolean>(false)
  const { fileUrl, fileToUpload, name, unique } = value

  useEffect(() => {
    if (!isValidFile) {
      handleChange('fileToUpload', null)
    }
  }, [isValidFile])

  const handleChange = (field: keyof IAllianceBrochureCreate, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue
    })
  }

  const handleClickDelete = () => {
    if (unique && onDelete) {
      onDelete(unique)
    }
  }

  const handleValidateFile = useCallback((isValid: boolean) => {
    setIsValidFile(isValid)
  }, [])

  return (
    <div className="brochure-from--layout">
      <div className='brochure-form--link-copy'>
        <LabelComponent>
          Url del folleto
        </LabelComponent>
        {!!fileUrl ? (
          <CopyTextComponent
            text={fileUrl}
            linkToText
          />
        ) : (
          <TextComponent type="text">
            No cargado
          </TextComponent>
        )}
      </div>
      <InputComponent
        onChange={(e) => handleChange('name', e.target.value)}
        value={name}
        label="Nombre"
      />
      <InputFileComponent
        onChange={(e) => handleChange('fileToUpload', e.target.files?.[0])}
        as="file"
        value={fileToUpload}
        label="Archivo"
        extensions={['pdf', 'gif', 'jpg', 'png']}
        accept=".pdf, .gif, .jpg, .png"
        onValidateExtension={handleValidateFile}
      />
      <MdDeleteForever
        onClick={handleClickDelete}
        className='brochure-from--icon'
      />
    </div>
  )
}

export default ModalAllianceBrochureFormModuleLayout
